import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutGrid, Cpu, Radio, Bell, BarChart3, FileText, MapPin, Settings as SettingsIcon,
  Inbox, Gauge, Menu, X, Sun, Moon, Monitor, Languages, LogOut, ChevronDown, MonitorPlay,
  SlidersHorizontal,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { LANGUAGES, setLanguage } from '../lib/i18n';
import { useLive } from '../lib/ws';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { Badge, Button, useToast } from './ui';
import ScopeSwitcher from './ScopeSwitcher';
import ImpersonationBanner from './ImpersonationBanner';

type NavItem = { to: string; label: string; icon: typeof LayoutGrid; permission?: string; badge?: number };

export default function Layout() {
  const { t } = useTranslation('nav');
  const { user, tenant, org, scope, can, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const toast = useToast();

  /*
   * The application-wide socket.
   *
   * This used to be opened purely for the "live" dot in the header, with no
   * message handler at all — so every alarm the server pushed arrived here and
   * was dropped on the floor. An operator learned about a critical alarm only
   * when the thirty-second badge poll below caught up, or when they happened
   * to reload the page. The events were always being sent; nothing was
   * listening to them.
   *
   * Handling them here rather than on the alarms page is deliberate: the whole
   * point is to be told while you are looking at something else.
   */
  const { connected } = useLive({
    onMessage: (message) => {
      switch (message.type) {
        case 'alarm:raised': {
          const alarm = message.alarm ?? {};
          refreshAlarmViews();
          // A maintenance window means someone has explicitly asked not to be
          // alerted. The alarm is still recorded and the badge still moves —
          // it just does not interrupt.
          if (alarm.details?.suppressed) break;
          const notify = alarm.severity === 'critical' ? toast.error
            : alarm.severity === 'warning' ? toast.warn
            : toast.info;
          notify(alarm.label ?? 'Alarm raised',
            [alarm.device_name ?? alarm.dev_eui, alarm.location_name].filter(Boolean).join(' — ') || undefined);
          break;
        }
        case 'alarm:cleared':
          refreshAlarmViews();
          break;
        case 'device:discovered':
          // Same class of bug, same fix: the discovery inbox was also waiting
          // on a poll to notice a sensor that had already reported.
          void queryClient.invalidateQueries({ queryKey: ['discovered-count'] });
          void queryClient.invalidateQueries({ queryKey: ['discovered'] });
          break;
        default:
          break;
      }
    },
  });

  /** The badge here, and the alarms page if it happens to be open. */
  function refreshAlarmViews() {
    void queryClient.invalidateQueries({ queryKey: ['alarm-summary'] });
    void queryClient.invalidateQueries({ queryKey: ['alarms'] });
    void queryClient.invalidateQueries({ queryKey: ['alarm-priority'] });
  }

  // Counts that belong in the navigation itself: an operator should see
  // there is something waiting without opening the page.
  const { data: alarmSummary } = useQuery({
    queryKey: ['alarm-summary'],
    queryFn: () => api.get('/api/alarms/summary'),
    refetchInterval: 30_000,
  });
  const { data: workOrders } = useQuery({
    queryKey: ['work-orders-summary'],
    queryFn: () => api.get('/api/work-orders/summary'),
    refetchInterval: 60_000,
    enabled: can('work_orders:read'),
  });
  const { data: discovered } = useQuery({
    queryKey: ['discovered-count'],
    queryFn: () => api.get('/api/devices/discovered'),
    refetchInterval: 30_000,
    enabled: can('devices:read'),
  });

  // Close the account menu when the user clicks anywhere else. Subscribing to
  // an external event is what an effect is for; setting state from the
  // pathname during an effect is not.
  useEffect(() => {
    if (!accountOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [accountOpen]);

  const items: NavItem[] = [
    { to: '/', label: t('overview'), icon: Gauge },
    { to: '/dashboards', label: t('dashboards'), icon: LayoutGrid },
    { to: '/devices', label: t('devices'), icon: Cpu },
    { to: '/discovery', label: t('discovery'), icon: Inbox, badge: discovered?.length },
    { to: '/gateways', label: t('gateways'), icon: Radio },
    { to: '/alarms', label: t('alarms'), icon: Bell, badge: alarmSummary?.open },
    { to: '/controls', label: t('controls'), icon: SlidersHorizontal, permission: 'controls:read' },
    { to: '/work-orders', label: t('workOrders'), icon: Wrench,
      permission: 'work_orders:read', badge: workOrders?.open },
    { to: '/analytics', label: t('analytics'), icon: BarChart3 },
    { to: '/playlists', label: 'Playlists', icon: MonitorPlay, permission: 'dashboards:read' },
    { to: '/reports', label: t('reports'), icon: FileText },
    { to: '/locations', label: t('locations'), icon: MapPin },
    { to: '/settings', label: t('settings'), icon: SettingsIcon, permission: 'settings:write' },
  ];

  // Outside an estate there is nothing in this navigation to show: the
  // platform and client consoles are separate routes with their own shape.
  const visible = scope?.kind === 'tenant'
    ? items.filter((item) => !item.permission || can(item.permission))
    : [];

  const navLink = (item: NavItem, onClick?: () => void) => (
    <NavLink
      key={item.to} to={item.to} end={item.to === '/'} onClick={onClick}
      className={({ isActive }) => cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-brand-50 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200'
          : 'text-muted hover:bg-[rgb(var(--surface-raised))] hover:text-app'
      )}
    >
      <item.icon size={18} className="shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge ? (
        <Badge tone={item.to === '/alarms' ? 'critical' : 'brand'} className="tabular-nums">{item.badge}</Badge>
      ) : null}
    </NavLink>
  );

  const themeIcon = theme === 'dark' ? <Moon size={16} /> : theme === 'light' ? <Sun size={16} /> : <Monitor size={16} />;

  return (
    <div className="min-h-screen flex flex-col">
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar: fixed on desktop, a drawer on phones. */}
      <aside className={cn(
        'fixed inset-y-0 start-0 z-40 w-64 surface app-border border-e flex-col',
        'transition-transform lg:translate-x-0 lg:static lg:flex',
        menuOpen ? 'flex translate-x-0' : 'hidden lg:flex'
      )}>
        <div className="flex items-center gap-2.5 px-4 h-14 app-border border-b shrink-0">
          <img src={org?.branding?.logoUrl || '/logo.svg'} alt="" className="h-7 w-7 object-contain" />
          <div className="min-w-0">
            <p className="font-semibold text-app leading-tight truncate">
              {org?.branding?.productName || 'iBMS'}
            </p>
            <p className="text-[11px] text-muted truncate">{tenant?.name}</p>
          </div>
          <button className="ms-auto lg:hidden text-muted" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5" aria-label="Main">
          {visible.map((item) => navLink(item, () => { setMenuOpen(false); setAccountOpen(false); }))}
        </nav>

        <div className="p-2 app-border border-t shrink-0 space-y-1">
          <ScopeSwitcher />
          <div className="relative" ref={accountRef}>
            <button
              onClick={() => setAccountOpen((v) => !v)}
              className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 hover:bg-[rgb(var(--surface-raised))]"
              aria-expanded={accountOpen}
            >
              <span className="h-7 w-7 rounded-full bg-brand-600 text-white text-xs font-semibold grid place-items-center shrink-0">
                {(user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1 text-start">
                <span className="block text-sm text-app truncate">{user?.name ?? user?.email}</span>
                <span className="block text-[11px] text-muted capitalize">{user?.role}</span>
              </span>
              <ChevronDown size={14} className={cn('text-muted transition-transform', accountOpen && 'rotate-180')} />
            </button>

            {accountOpen && (
              <div className="absolute bottom-full mb-2 start-0 end-0 surface app-border border rounded-lg shadow-lg p-1.5 space-y-1">
                <div className="px-2 py-1.5">
                  <p className="text-[11px] font-medium text-muted uppercase tracking-wide mb-1.5">Appearance</p>
                  <div className="flex gap-1">
                    {(['light', 'dark', 'system'] as const).map((option) => (
                      <button key={option} onClick={() => setTheme(option)}
                        className={cn('flex-1 rounded-md px-2 py-1.5 text-xs capitalize',
                          theme === option ? 'bg-brand-600 text-white' : 'hover:bg-[rgb(var(--surface-raised))] text-muted')}>
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="px-2 py-1.5">
                  <p className="text-[11px] font-medium text-muted uppercase tracking-wide mb-1.5 flex items-center gap-1">
                    <Languages size={11} /> Language
                  </p>
                  <div className="flex gap-1">
                    {LANGUAGES.map((language) => (
                      <button key={language.code} onClick={() => setLanguage(language.code)}
                        className={cn('flex-1 rounded-md px-2 py-1.5 text-xs',
                          i18n.language === language.code ? 'bg-brand-600 text-white' : 'hover:bg-[rgb(var(--surface-raised))] text-muted')}>
                        {language.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={() => void logout()}
                  className="w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm text-critical hover:bg-red-50 dark:hover:bg-red-950/30">
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {menuOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMenuOpen(false)} aria-hidden />
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 surface app-border border-b h-14 flex items-center gap-3 px-4 shrink-0">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Menu size={20} />
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs" title={connected ? 'Receiving live updates' : 'Not connected'}>
            <span className={cn('h-2 w-2 rounded-full', connected ? 'bg-ok animate-pulse' : 'bg-warning')} />
            <span className="text-muted hidden sm:inline">{connected ? 'Live' : 'Reconnecting…'}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle theme" className="hidden sm:inline-flex">
            {themeIcon}
          </Button>
        </header>

        <main className="flex-1 min-w-0 p-4 sm:p-6 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
        </div>
      </div>
    </div>
  );
}
